import {createFileRoute, Link, useLoaderData} from '@tanstack/react-router';
import {Button} from '@/components/ui/button';
import {Footer} from '@/components/ui/footer';
import {Navbar} from '@/components/ui/navbar';

import {
  Search,
  MapPin,
  FileText,
  Layers,
  CheckCircle2,
  ArrowRight,
  Map,
  Check,
  Clock,
  Download,
  Users,
  Info,
} from 'lucide-react';
import {MapSearchWidget} from '@/components/mapSearchWidget/mapSearchWidget';
import {getUserLocationFromIp} from '@/server/getUserLocationFromIp';
import {getEventsByBounds} from '@/queries/getEventsByBounds';
import {getUpcomingEvents} from '@/queries/getUpcomingEvents';
import {boundsFromCenter} from '@/utils/geoBounds';
import {Typography} from '@/components/ui/typography';
import {HomeFeed} from '@/components/homeFeed/HomeFeed';
import {UpcomingEvents} from '@/components/upcomingEvents/upcomingEvents';
import {getProfileAttendingEvents} from '@/server/attendance';
import {getProfileRegisteredEvents} from '@/server/registration';
import {getEventParticipants} from '@/server/registration';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://wayra.cc';

/**
 * Zoom used for the server-side bounds query. Lower than the render zoom
 * because boundsFromCenter assumes a 512px viewport while the actual map
 * container is much wider. Using a lower zoom ensures we fetch events for
 * the full visible area.
 */
const HOMEPAGE_QUERY_ZOOM = 9;

/** How far (px) the hero map widget overlaps into the next section on lg+ */
const HERO_OVERLAP_PX = 120;

export const Route = createFileRoute('/')({
  component: Home,
  head: () => ({
    links: [{rel: 'canonical', href: `${SITE_URL}/`}],
  }),
  beforeLoad: ({context}) => {
    const showHomeFeed =
      !!context.profile && context.featureFlags['enable-home-feed'];
    return {showHomeFeed};
  },
  loader: async ({context}) => {
    if (context.showHomeFeed) {
      const [attending, registered] = await Promise.all([
        getProfileAttendingEvents(),
        getProfileRegisteredEvents(),
      ]);

      const now = new Date();
      const seen = new Set<string>();
      const merged = [...registered, ...attending].filter((e) => {
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        return e.startDate && new Date(e.startDate) >= now;
      });
      merged.sort(
        (a, b) =>
          new Date(a.startDate!).getTime() -
          new Date(b.startDate!).getTime(),
      );

      const nextEvent = merged[0] ?? null;
      const participants = nextEvent
        ? await getEventParticipants({data: nextEvent.id})
        : null;

      return {showHomeFeed: true as const, nextEvent, participants};
    }

    const userLocation = await getUserLocationFromIp();
    const hasUserLocation = userLocation.lat !== 0 || userLocation.lng !== 0;
    const resolvedLocation = hasUserLocation
      ? userLocation
      : {lat: 40.7128, lng: -73.906};
    const city = hasUserLocation ? (userLocation.city ?? null) : 'NYC';

    const bounds = boundsFromCenter(resolvedLocation, HOMEPAGE_QUERY_ZOOM);
    const southWest = {lat: bounds.swLat, lng: bounds.swLng};
    const northEast = {lat: bounds.neLat, lng: bounds.neLng};

    const [boundsResult, upcomingEvents] = await Promise.all([
      getEventsByBounds({
        data: {
          southWest,
          northEast,
          page: 1,
          pageSize: 100,
        },
      }),
      getUpcomingEvents({data: {southWest, northEast}}),
    ]);

    return {
      showHomeFeed: false as const,
      events: boundsResult.events,
      ghostEvents: boundsResult.ghostEvents,
      userLocation: resolvedLocation,
      upcomingEvents,
      city,
    };
  },
});

function Home() {
  const loaderData = useLoaderData({from: '/'});

  if (loaderData.showHomeFeed) {
    return (
      <HomeFeed
        nextEvent={loaderData.nextEvent}
        participants={loaderData.participants}
      />
    );
  }

  const {events, ghostEvents, userLocation, upcomingEvents, city} =
    loaderData;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      {/* Hero Section */}
      <section
        className="relative -mt-[88px] pt-[88px] overflow-visible bg-cover bg-center"
        style={{backgroundImage: "url('/hero_blur.jpg')"}}
      >
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative px-page py-12 lg:py-20">
          <div
            className="flex gap-12 flex-col lg:flex-row items-center lg:items-start"
            style={{marginBottom: `${-HERO_OVERLAP_PX}px`}}
          >
            {/* Text Content */}
            <div className="space-y-6 flex flex-col items-center lg:items-start">
              {/* Heading */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl text-pretty text-center lg:text-left font-black italic text-white leading-tight">
                Tuesday Night Crits to 1,200km Brevets.
              </h1>

              {/* Description */}
              <Typography
                level="S1"
                className="text-neutral-100 text-center text-pretty lg:text-left"
              >
                One search for every race, gravel ride and brevet near you. Featured picks from people who actually ride them, so your weekend goes to the good ones.
              </Typography>
            </div>
            {/* CTA Buttons */}
            <MapSearchWidget
              events={events}
              ghostEvents={ghostEvents}
              userLocation={userLocation}
            />
          </div>
        </div>
      </section>

      {/* Coming Up Section */}
      <section
        className="pb-20 bg-white"
        style={{paddingTop: `calc(3rem + ${HERO_OVERLAP_PX}px)`}}
      >
        <UpcomingEvents events={upcomingEvents} city={city} />
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="px-page">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-4">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 rounded-full px-4 py-1.5 text-sm font-medium mb-4">
              Built By Cyclists Who Get It
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              The Three Things That Actually Matter
            </h2>
            <p className="text-lg text-slate-600">
              Based on feedback from hundreds of frustrated cyclists, we focused
              on the essentials.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-3 gap-8 mt-12 max-w-6xl mx-auto">
            {/* One Search */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <div className="w-14 h-14 bg-orange-50 rounded-xl flex items-center justify-center mb-6">
                <Search className="w-7 h-7 text-orange-500" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                One Search.
                <br />
                Every Platform.
              </h3>
              <p className="text-slate-600 mb-6">
                We aggregate events from BikeReg, Active.com, RUSA, regional
                calendars, and individual event sites. Search once, see
                everything, sorted by distance from you.
              </p>
              <ul className="space-y-4 text-slate-600">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span>
                    Events from all major platforms in one searchable list
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span>Map view showing every event within your radius</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span>Alerts when new events are added in your area</span>
                </li>
              </ul>
              <div className="flex flex-wrap gap-2 mt-6">
                <span className="px-4 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-full">
                  BikeReg
                </span>
                <span className="px-4 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-full">
                  Active.com
                </span>
                <span className="px-4 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-full">
                  RUSA
                </span>
                <span className="px-4 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-full">
                  & More
                </span>
              </div>
            </div>

            {/* See Everything Upfront */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <div className="w-14 h-14 bg-violet-100 rounded-xl flex items-center justify-center mb-6">
                <Info className="w-7 h-7 text-violet-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                See Everything
                <br />
                Upfront
              </h3>
              <p className="text-slate-600 mb-6">
                No more buried details or vague descriptions. Get all the info
                you need to decide if an event is right for you, before you
                register.
              </p>
              <ul className="space-y-4 text-slate-600">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span>
                    Distance, elevation, participant count, spots left
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span>Clear skill levels and "what to expect" guides</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span>Parking, check-in times, and logistics info</span>
                </li>
              </ul>
              <div className="flex flex-wrap gap-2 mt-6">
                <span className="px-4 py-2 text-sm font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-full">
                  Full Details
                </span>
                <span className="px-4 py-2 text-sm font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-full">
                  Live Capacity
                </span>
                <span className="px-4 py-2 text-sm font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-full">
                  Race Prep
                </span>
              </div>
            </div>

            {/* No More Route Hunts */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <div className="w-14 h-14 bg-green-50 rounded-xl flex items-center justify-center mb-6">
                <Download className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                No More
                <br />
                Route Hunts
              </h3>
              <p className="text-slate-600 mb-6">
                GPX files, maps, and elevation profiles are on the event page
                and in your dashboard the moment you register. No digging
                through emails or external sites.
              </p>
              <ul className="space-y-4 text-slate-600">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span>One-click GPX download for your bike computer</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span>Interactive map with turn-by-turn preview</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <span>Elevation profile and terrain breakdown</span>
                </li>
              </ul>
              <div className="flex flex-wrap gap-2 mt-6">
                <span className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-full">
                  GPX Files
                </span>
                <span className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-full">
                  Course Maps
                </span>
                <span className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-full">
                  No Hunting
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Event Organizers Section */}
      <section className="py-20 bg-white">
        <div className="px-page">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 rounded-full px-4 py-1.5 text-sm font-medium mb-4">
                <span>🎊</span>
                For Event Organizers
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Riders Are Looking For Events Like Yours. Help Them Find It.
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Tired of platforms that make your events invisible? We give
                organizers the tools to create beautiful, detailed event pages
                that riders actually want to see. While making it easy for
                participants to find you.
              </p>

              <ul className="space-y-5">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 mt-0.5 shrink-0" />
                  <div>
                    <span className="font-semibold text-slate-900">
                      Reach more riders with map-based discovery
                    </span>
                    <p className="text-slate-600">
                      Your events show up in location searches and alerts
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 mt-0.5 shrink-0" />
                  <div>
                    <span className="font-semibold text-slate-900">
                      Create rich, detailed event pages
                    </span>
                    <p className="text-slate-600">
                      Photos, course maps, logistics. Everything riders need to
                      decide
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 mt-0.5 shrink-0" />
                  <div>
                    <span className="font-semibold text-slate-900">
                      Upload route files directly to event pages
                    </span>
                    <p className="text-slate-600">
                      No more fielding emails asking for GPX files
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 mt-0.5 shrink-0" />
                  <div>
                    <span className="font-semibold text-slate-900">
                      Fair pricing when you host registration with us
                    </span>
                    <p className="text-slate-600">
                      5% + payment processing. Keep more of your fees.
                    </p>
                  </div>
                </li>
              </ul>

              <div className="mt-8">
                <Button
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 py-3"
                  asChild
                >
                  <Link
                    to="/organizers"
                    data-mp-element-name="organizers-learn-more-cta"
                    data-mp-cta-location="organizers-section"
                    data-mp-cta-variant="primary"
                    data-mp-cta-destination="/organizers"
                  >
                    Learn More About Organizing
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right Content - Mockup */}
            <div className="bg-gradient-to-br from-slate-100 to-slate-50 rounded-3xl p-8 relative">
              {/* Create Event Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="font-semibold text-slate-900 text-lg">
                    Create Event
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="h-3 bg-slate-100 rounded-full w-full"></div>
                  <div className="h-3 bg-slate-100 rounded-full w-4/5"></div>
                  <div className="h-3 bg-slate-100 rounded-full w-full"></div>
                  <div className="h-3 bg-slate-100 rounded-full w-3/5"></div>
                </div>
              </div>

              {/* Upload Routes Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Map className="w-6 h-6 text-green-600" />
                  </div>
                  <span className="font-semibold text-slate-900 text-lg">
                    Upload Routes
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="h-3 bg-slate-100 rounded-full w-full"></div>
                  <div className="h-3 bg-slate-100 rounded-full w-3/4"></div>
                  <div className="h-3 bg-slate-100 rounded-full w-5/6"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-slate-50 border-y border-slate-100">
        <div className="px-page">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Search className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="font-bold text-slate-900">
                Events from 5+ Platforms
              </div>
              <div className="text-sm text-slate-500">
                BikeReg, Active, RUSA & more
              </div>
            </div>
            <div>
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <div className="font-bold text-slate-900">Built by Cyclists</div>
              <div className="text-sm text-slate-500">We feel your pain</div>
            </div>
            <div>
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
              </div>
              <div className="font-bold text-slate-900">Always Growing</div>
              <div className="text-sm text-slate-500">
                New events added daily
              </div>
            </div>
            <div>
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <div className="font-bold text-slate-900">Community Powered</div>
              <div className="text-sm text-slate-500">
                Built with rider feedback
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search CTA Section */}
      <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="px-page">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Ready to Find Your Next Event?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Search hundreds of cycling events on one interactive map. Filter
              by distance, discipline, and date to find exactly what you're
              looking for.
            </p>
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-base font-medium"
              asChild
            >
              <Link
                to="/search"
                data-mp-element-name="find-events-cta"
                data-mp-cta-location="bottom-cta"
                data-mp-cta-variant="primary"
                data-mp-cta-destination="/search"
              >
                Explore the Map
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>

        <div className="px-page mt-16">
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <MapPin className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">
                Map-Based Discovery
              </h3>
              <p className="text-sm text-slate-600">
                Find events within miles of your location
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Download className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">
                Instant Route Files
              </h3>
              <p className="text-sm text-slate-600">
                GPX downloads on event pages, no hunting
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Layers className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">
                All Platforms, One Place
              </h3>
              <p className="text-sm text-slate-600">
                BikeReg, Active, RUSA and more, aggregated
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
